import contracts from "./contracts.testnet.json";
import { bytesToHex } from "./trader-crypto.js";

export const TESTNET_NODE = "https://testnet.nodes.dusk.network";
export const TESTNET_GRAPHQL = `${TESTNET_NODE}/on/graphql/query`;

/** `rights-registry` / `prediction-market-logic` create gate (`achievement-nft-types`). */
export const RIGHT_CREATE_MARKET = 1;

const ONE_DUSK = 1_000_000_000n;

/**
 * Worst-case gas reserve for a deposit/withdraw call, in lux. `deposit_collateral`/
 * `withdraw_collateral` are contract calls, not plain transfers - rusk-wallet's own
 * `DEFAULT_LIMIT_CALL` (2,000,000,000 gas) × `DEFAULT_PRICE` (1 lux/gas) is the
 * ceiling a wallet would set aside by default for one (see rusk-wallet/src/gas.rs).
 * Actual gas used (and therefore the real fee) is typically far less - the limit
 * only bounds the worst case - but reserving the full ceiling on Deposit's "Max"
 * guarantees the wallet is never left unable to afford the tx that spends it.
 */
export const GAS_RESERVE_LUX = 2_000_000_000n;

export function toHexContractId(id) {
  const s = String(id || "").trim();
  return s.toLowerCase().startsWith("0x") ? s : `0x${s}`;
}

export function luxToDusk(lux) {
  // Mark-to-market and similar callers can pass fractional lux - round first.
  const n = typeof lux === "bigint" ? lux : BigInt(Math.round(Number(lux) || 0));
  return Number((n * 1000n) / ONE_DUSK) / 1000;
}

export function duskToLux(dusk) {
  return BigInt(Math.round(Number(dusk) * 1e9));
}

function preset(key, name, methodSigs) {
  const entry = contracts[key];
  if (!entry?.contractId || !entry?.driverUrl) return null;
  return {
    contractId: toHexContractId(entry.contractId),
    driverUrl: entry.driverUrl,
    name,
    methodSigs,
  };
}

function configuredContract(key) {
  const entry = contracts[key];
  const id = String(entry?.contractId || "").trim();
  const driver = String(entry?.driverUrl || "").trim();
  return Boolean(id && driver);
}

/** True when `contracts.testnet.json` pins a live `achievementNft` contract + driver. */
export function achievementNftConfigured() {
  return configuredContract("achievementNft");
}

/** True when `contracts.testnet.json` pins a live `rightsRegistry` contract + driver. */
export function rightsRegistryConfigured() {
  return configuredContract("rightsRegistry");
}

/**
 * PM data contract preset for free reads (`get_participation_bond`, …).
 * `scripts/sync-prediction-market-frontend.sh` copies `prediction-market-data.wasm`
 * and writes `predictionMarketData` when the pin (or crate `wasm-dd` fallback) exists.
 */
function predictionMarketDataPreset() {
  const entry = contracts.predictionMarketData;
  const contractId =
    entry?.contractId || contracts.predictionMarket?.dataContractId;
  const driverUrl = entry?.driverUrl;
  if (!contractId || !driverUrl) return null;
  return {
    contractId: toHexContractId(contractId),
    driverUrl,
    name: "predictionMarketData",
    methodSigs: {
      get_participation_bond: "get_participation_bond(Pk96)",
      get_last_market_created: "get_last_market_created(Pk96)",
      get_markets_created_by: "get_markets_created_by(Pk96)",
    },
  };
}

function moonlightPkBytes96Arg(pkBytes96) {
  const bytes =
    pkBytes96 instanceof Uint8Array ? pkBytes96 : new Uint8Array(pkBytes96);
  if (bytes.length !== 96) {
    throw new Error("Moonlight public key must be 96 bytes");
  }
  return bytes;
}

/**
 * prediction-market resolution-v2 presets. Multi-arg calls use positional arrays;
 * struct args use named objects (data-driver schema).
 *
 * Must return a **named object** (not an array) - `createDuskApp` spreads
 * `contracts` into `Record<string, …>` and looks up by preset key.
 */
export function buildContractPresets() {
  const out = {};
  const predictionMarket = preset("predictionMarket", "predictionMarket", {
    market_info: "market_info(u64)",
    resolution_of: "resolution_of(u64)",
    price_yes: "price_yes(u64)",
    position_of: "position_of(u64,[u8;32])",
    collateral_of: "collateral_of([u8;32])",
    next_nonce: "next_nonce([u8;32])",
    trader_commitment_of: "trader_commitment_of(BlsPublicKey)",
    kyc_mode: "kyc_mode()",
    deposit_collateral: "deposit_collateral(DepositArgs)",
    withdraw_collateral: "withdraw_collateral(WithdrawArgs)",
    trade: "trade(TradeArgs)",
    claim_payout: "claim_payout(ClaimArgs)",
    redeem_cancelled: "redeem_cancelled(ClaimArgs)",
    create_market: "create_market(CreateMarketArgs)",
    assert_yes: "assert_yes(u64)",
    dispute_yes: "dispute_yes(DisputeYesArgs)",
    challenge_no: "challenge_no(ChallengeNoArgs)",
    resolve: "resolve(ResolveArgs)",
    finalize_resolution: "finalize_resolution(u64)",
    cancel_market: "cancel_market(u64)",
    sweep_losing_reserve: "sweep_losing_reserve(u64)",
    claim_lp_fees: "claim_lp_fees(u64)",
    participation_bond_amount: "participation_bond_amount()",
    post_participation_bond: "post_participation_bond()",
    withdraw_participation_bond: "withdraw_participation_bond()",
    allowed_categories: "allowed_categories()",
  });
  if (predictionMarket) out.predictionMarket = predictionMarket;

  const achievementNft = preset("achievementNft", "achievementNft", {
    claim: "claim(ClaimArgs)",
    streak_root: "streak_root(u64)",
    holds_type: "holds_type(Account,u32)",
    has_claimed: "has_claimed([u8;32],u32)",
  });
  if (achievementNft) out.achievementNft = achievementNft;

  const rightsRegistry = preset("rightsRegistry", "rightsRegistry", {
    has_right: "has_right(BlsPublicKey,u64)",
  });
  if (rightsRegistry) out.rightsRegistry = rightsRegistry;

  const predictionMarketData = predictionMarketDataPreset();
  if (predictionMarketData) out.predictionMarketData = predictionMarketData;

  return out;
}

/**
 * On-chain `has_right(moonlightPk, RIGHT_CREATE_MARKET)` via rights-registry.
 * Returns false when the registry is not pinned (pre-Phase G).
 */
export async function walletHasCreateRight(dusk, moonlightPkBase58) {
  if (!rightsRegistryConfigured()) return false;
  const address = String(moonlightPkBase58 || "").trim();
  if (!address) return false;
  try {
    return Boolean(
      await dusk.readContract({
        contract: "rightsRegistry",
        functionName: "has_right",
        args: [address, RIGHT_CREATE_MARKET],
      })
    );
  } catch {
    return false;
  }
}

/**
 * Creator participation bond: required amount from PM logic, posted amount
 * from PM data (`get_participation_bond` on `dataContractId`).
 */
export async function readParticipationBondState(dusk, moonlightPkBytes96) {
  if (!contracts.predictionMarket?.contractId) {
    throw new Error(
      "Prediction market not configured - redeploy sync required."
    );
  }
  const pkBytes = moonlightPkBytes96Arg(moonlightPkBytes96);
  const required = BigInt(
    await dusk.readContract({
      contract: "predictionMarket",
      functionName: "participation_bond_amount",
      // Unit `()` — Connect encodes `[]` as a JSON sequence; driver wants `null`.
    })
  );

  if (!predictionMarketDataPreset()) {
    throw new Error(
      "Participation bond reads need predictionMarketData in contracts.testnet.json " +
        "(data contract id + data-driver URL) - not live until Phase G cutover wiring."
    );
  }

  const posted = BigInt(
    await dusk.readContract({
      contract: "predictionMarketData",
      functionName: "get_participation_bond",
      args: bytesToHex(pkBytes),
    })
  );
  return { required, posted };
}

export function predictionMarketContractId() {
  return contracts.predictionMarket?.contractId;
}

/** Contract owner / deployer Moonlight address. */
export function predictionMarketOwnerAddress() {
  return contracts.predictionMarket?.ownerAddress || "";
}

/** Create-admin Moonlight address (hand-edited after OPS init_create_admin). */
export function predictionMarketCreateAdminAddress() {
  return contracts.predictionMarket?.createAdminAddress || "";
}

/** Primary connected Moonlight account from wallet state, or "". */
export function connectedMoonlightAccount(wallet) {
  const profiles = wallet?.state?.profiles;
  if (!Array.isArray(profiles) || !profiles.length) return "";
  return String(profiles[0]?.account || profiles[0]?.address || "").trim();
}

/** True when the connected wallet is the prediction-market contract owner. */
export function isContractOwner(wallet) {
  const owner = predictionMarketOwnerAddress();
  const account = connectedMoonlightAccount(wallet);
  return Boolean(owner && account && owner === account);
}

/** True when the connected wallet may create markets (owner or create-admin). */
export function isPrivilegedCreator(wallet) {
  const account = connectedMoonlightAccount(wallet);
  if (!account) return false;
  const owner = predictionMarketOwnerAddress();
  const admin = predictionMarketCreateAdminAddress();
  return Boolean(
    (owner && account === owner) || (admin && account === admin)
  );
}
